"use client";

import { c as _c } from "react/compiler-runtime";
import { isRegularCatalogShopItem, lineFromRegularItem } from "../catalog/cart-lines";
import CatalogItemDetailsDialog from "../catalog/item-details-dialog";
import { CornerRibbon, priceForShopItem, savingsForShopItem } from "../components/catalog-card-primitives";
import GiftCardConfiguratorDialog, { GiftCardConfiguratorSelection } from "../gift-cards/configurator-dialog";
import MembershipConfiguratorDialog from "../memberships/configurator-dialog";
import PackageDetailsDialog from "../packages/details-dialog";
import { packagePricingSummary } from "../packages/pricing";
import { packageLineFromItem } from "../packages/utils";
import { getCatalogPromotionPresentation } from "../promotion-messaging";
import React, { useState } from "react";
import { FiActivity, FiArrowRight, FiCalendar, FiRepeat, FiShoppingBag, FiTag } from "react-icons/fi";
import { GiftCorner, GuillocheOrnament } from "@/components/billing/gift-card-tile";
import Button from "@/components/ui/button";
import { formatCurrency } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { ItemType } from "@/types/enums/item-type";
import { RegularCatalogItemType, RegularCatalogShopItem, ShopCartLine, ShopItem, isCustomAmountGiftCard } from "@/types/shop";
import ParcelCorner, { ParcelHeroArtwork } from "./parcel-corner";
import ShopCardDecoration from "./shop-card-decoration";
type FeaturedHeroSurface = "gift-card" | "package" | "membership" | "service" | "product" | "medication";
type RegularFeaturedHeroSurface = Exclude<FeaturedHeroSurface, "gift-card" | "package">;
interface FeaturedRegularHeroVariant {
  surface: RegularFeaturedHeroSurface;
  eyebrow: string;
  actionLabel: string;
  Icon: React.ComponentType<{
    className?: string;
  }>;
  accentClassName: string;
}
const REGULAR_HERO_VARIANTS: Record<RegularCatalogItemType, FeaturedRegularHeroVariant> = {
  [ItemType.SERVICE]: {
    surface: "service",
    eyebrow: "Featured service",
    actionLabel: "View service",
    Icon: FiCalendar,
    accentClassName: "bg-[radial-gradient(circle_at_18%_0%,rgb(var(--primary)_/_0.16),transparent_56%)]"
  },
  [ItemType.PRODUCT]: {
    surface: "product",
    eyebrow: "Featured product",
    actionLabel: "View product",
    Icon: FiShoppingBag,
    accentClassName: "bg-[radial-gradient(circle_at_18%_0%,rgb(var(--primary)_/_0.12),transparent_56%)]"
  },
  [ItemType.MEDICATION]: {
    surface: "medication",
    eyebrow: "Featured medication",
    actionLabel: "View medication",
    Icon: FiActivity,
    accentClassName: "bg-[radial-gradient(circle_at_18%_0%,rgb(var(--primary)_/_0.10),transparent_56%)]"
  }
};
const GiftCardHeroArtwork: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div data-testid="gift-card-artwork"><div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_0%,rgb(var(--primary-foreground)_/_0.22),transparent_55%)]" /><div className="absolute inset-0 bg-gradient-to-t from-primary-foreground/15 via-transparent to-transparent" /><GuillocheOrnament tone="primary" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const RegularHeroArtwork: React.FC<{
  variant: FeaturedRegularHeroVariant;
}> = t0 => {
  const $ = _c(12);
  const {
    variant
  } = t0;
  const Icon = variant.Icon;
  const t1 = `${variant.surface}-artwork`;
  let t2;
  if ($[0] !== variant.accentClassName) {
    t2 = cx("absolute inset-0", variant.accentClassName);
    $[0] = variant.accentClassName;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <div className={t2} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute -bottom-24 -right-20 h-64 w-64 rounded-full border border-primary/10" />;
    t5 = <div className="absolute -bottom-16 -right-12 h-44 w-44 rounded-full border border-primary/10" />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] !== Icon) {
    t6 = <div className="absolute right-7 top-8 flex h-14 w-14 rotate-3 items-center justify-center rounded-2xl border border-primary/15 bg-background/70 text-primary shadow-sm backdrop-blur-sm"><Icon aria-hidden={true} className="h-7 w-7" /></div>;
    $[6] = Icon;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t1 || $[9] !== t3 || $[10] !== t6) {
    t7 = <div data-testid={t1}>{t3}{t4}{t5}{t6}</div>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
};
const RegularHeroCorner: React.FC<{
  variant: FeaturedRegularHeroVariant;
}> = t0 => {
  const $ = _c(2);
  const {
    variant
  } = t0;
  const Icon = variant.Icon;
  let t1;
  if ($[0] !== Icon) {
    t1 = <CornerRibbon><Icon className="h-5 w-5" /></CornerRibbon>;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const FeaturedItemHeroFrame: React.FC<{
  surface: FeaturedHeroSurface;
  artwork: React.ReactNode;
  corner: React.ReactNode;
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(11);
  const {
    surface,
    artwork,
    corner,
    children
  } = t0;
  const t1 = surface === "gift-card" ? "border-primary bg-primary text-primary-foreground" : "border-primary/20 bg-background";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("relative mb-14 overflow-hidden rounded-3xl border shadow-[0_2px_8px_-2px_rgb(var(--foreground)_/_0.15),0_20px_50px_-28px_rgb(var(--foreground)_/_0.35)]", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== artwork || $[3] !== corner || $[4] !== surface) {
    t3 = <ShopCardDecoration frame="hero" tone={surface} artwork={artwork} corner={corner} />;
    $[2] = artwork;
    $[3] = corner;
    $[4] = surface;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== children || $[7] !== surface || $[8] !== t2 || $[9] !== t3) {
    t4 = <section data-testid="featured-item-hero" data-featured-item-family={surface} aria-labelledby="featured-shop-item-title" className={t2}>{t3}{children}</section>;
    $[6] = children;
    $[7] = surface;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const FeaturedHeroContent: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="relative flex min-h-[300px] flex-col px-6 pb-7 pt-16 sm:px-8 sm:pb-8 md:min-h-[340px] md:px-12 md:pb-10 md:pt-20">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const FeaturedHeroTitle: React.FC<{
  surface: FeaturedHeroSurface;
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(5);
  const {
    surface,
    children
  } = t0;
  const t1 = surface === "gift-card" ? "text-[2rem] leading-[1.08] text-primary-foreground sm:text-[2.5rem]" : "mt-2 max-w-3xl text-[2rem] leading-[1.05] text-foreground sm:text-[2.5rem] md:text-[3rem]";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("font-serif font-medium tracking-tight", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== t2) {
    t3 = <h1 id="featured-shop-item-title" className={t2}>{children}</h1>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const FeaturedHeroDescription: React.FC<{
  surface: FeaturedHeroSurface;
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(5);
  const {
    surface,
    children
  } = t0;
  const t1 = surface === "gift-card" ? "mt-2 text-primary-foreground/70" : "mt-4 text-foreground-secondary";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("line-clamp-6 max-w-prose whitespace-pre-line text-body-lg leading-relaxed", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== t2) {
    t3 = <p data-testid="featured-item-description" className={t2}>{children}</p>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const FeaturedHeroFooter: React.FC<{
  surface: FeaturedHeroSurface;
  children: React.ReactNode;
  action: React.ReactNode;
}> = t0 => {
  const $ = _c(6);
  const {
    surface,
    children,
    action
  } = t0;
  const t1 = surface === "gift-card" ? "border-primary-foreground/15" : "border-primary/15";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("mt-8 flex flex-col gap-5 border-t pt-5 sm:flex-row sm:items-end sm:justify-between", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== action || $[3] !== children || $[4] !== t2) {
    t3 = <div className={t2}>{children}{action}</div>;
    $[2] = action;
    $[3] = children;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const HeroActionButton: React.FC<{
  surface: FeaturedHeroSurface;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}> = t0 => {
  const $ = _c(14);
  const {
    surface,
    label,
    onClick,
    disabled
  } = t0;
  const t1 = surface === "gift-card" ? "secondary" : "primary";
  const t2 = !disabled && "cursor-pointer";
  const t3 = surface === "gift-card" && "border-primary-foreground/20 bg-background/95 text-primary md:hover:bg-background";
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = cx("min-h-11 w-full px-5 sm:w-auto", t2, t3);
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== disabled) {
    t5 = !disabled && <FiArrowRight aria-hidden={true} className="h-4 w-4" />;
    $[3] = disabled;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== label || $[6] !== t5) {
    t6 = <span className="inline-flex items-center gap-2">{label}{t5}</span>;
    $[5] = label;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== disabled || $[9] !== onClick || $[10] !== t1 || $[11] !== t4 || $[12] !== t6) {
    t7 = <Button variant={t1} size="base" onClick={onClick} disabled={disabled} className={t4}>{t6}</Button>;
    $[8] = disabled;
    $[9] = onClick;
    $[10] = t1;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};
const HeroPriceSummary: React.FC<{
  label?: string;
  price: number;
  originalPrice?: number;
  savings?: number;
  savingsLabel?: string;
}> = t0 => {
  const $ = _c(19);
  const {
    label: t1,
    price,
    originalPrice,
    savings: t2,
    savingsLabel: t3
  } = t0;
  const label = t1 === undefined ? "Price" : t1;
  const savings = t2 === undefined ? 0 : t2;
  const savingsLabel = t3 === undefined ? "Save" : t3;
  let t4;
  if ($[0] !== label) {
    t4 = <p className="text-overline text-foreground-secondary">{label}</p>;
    $[0] = label;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== price) {
    t5 = formatCurrency(price);
    $[2] = price;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t5) {
    t6 = <p className="font-serif text-[2rem] font-medium leading-none tracking-tight text-foreground tabular-nums">{t5}</p>;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== originalPrice || $[7] !== savings) {
    t7 = originalPrice !== undefined && savings > 0 && <p className="text-body text-foreground-secondary line-through tabular-nums">{formatCurrency(originalPrice)}</p>;
    $[6] = originalPrice;
    $[7] = savings;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <div className="mt-1 flex flex-wrap items-baseline gap-2">{t6}{t7}</div>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== savings || $[13] !== savingsLabel) {
    t9 = savings > 0 && <p className="mt-1 text-caption-emphasis text-primary">{savingsLabel} {formatCurrency(savings)}</p>;
    $[12] = savings;
    $[13] = savingsLabel;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t4 || $[16] !== t8 || $[17] !== t9) {
    t10 = <div>{t4}{t8}{t9}</div>;
    $[15] = t4;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
};
interface FeaturedItemVariantProps {
  item: ShopItem;
  onAddLine: (line: ShopCartLine) => void;
}
const FeaturedGiftCardHero: React.FC<FeaturedItemVariantProps> = t0 => {
  const $ = _c(31);
  const {
    item,
    onAddLine
  } = t0;
  const [selection, setSelection] = useState(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <GiftCardHeroArtwork />;
    t2 = <GiftCorner tone="primary" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-overline text-primary-foreground/65">Featured gift card</p>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== item) {
    t4 = isCustomAmountGiftCard(item) ? "Your amount" : formatCurrency(priceForShopItem(item));
    $[3] = item;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <div className="flex flex-1 flex-col justify-center py-4">{t3}<p className="mt-2 font-serif text-[2.75rem] font-medium leading-none tracking-tight text-primary-foreground drop-shadow-[0_1px_0_rgb(var(--foreground)_/_0.18)] tabular-nums sm:text-[3.5rem]">{t4}</p></div>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== item) {
    t6 = <HeroActionButton surface="gift-card" label="Choose gift card" onClick={() => setSelection(isCustomAmountGiftCard(item) ? {
      kind: "custom",
      item
    } : {
      kind: "item",
      item
    })} />;
    $[7] = item;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== item.name) {
    t7 = <FeaturedHeroTitle surface="gift-card">{item.name}</FeaturedHeroTitle>;
    $[9] = item.name;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== item.description) {
    t8 = item.description && <FeaturedHeroDescription surface="gift-card">{item.description}</FeaturedHeroDescription>;
    $[11] = item.description;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = <div className="min-w-0 max-w-3xl">{t7}{t8}</div>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t6 || $[17] !== t9) {
    t10 = <FeaturedHeroFooter surface="gift-card" action={t6}>{t9}</FeaturedHeroFooter>;
    $[16] = t6;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t5) {
    t11 = <FeaturedItemHeroFrame surface="gift-card" artwork={t1} corner={t2}><FeaturedHeroContent>{t5}{t10}</FeaturedHeroContent></FeaturedItemHeroFrame>;
    $[19] = t10;
    $[20] = t5;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== onAddLine) {
    t12 = line => {
      onAddLine(line);
      setSelection(null);
    };
    $[22] = onAddLine;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setSelection(null);
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== selection || $[26] !== t12) {
    t14 = <GiftCardConfiguratorDialog selection={selection} onConfirm={t12} onCancel={t13} />;
    $[25] = selection;
    $[26] = t12;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t11 || $[29] !== t14) {
    t15 = <>{t11}{t14}</>;
    $[28] = t11;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
};
const FeaturedPackageHero: React.FC<FeaturedItemVariantProps> = t0 => {
  const $ = _c(35);
  const {
    item,
    onAddLine
  } = t0;
  const [selection, setSelection] = useState(null);
  let t1;
  if ($[0] !== item) {
    t1 = packagePricingSummary(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    price,
    savings
  } = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ParcelHeroArtwork />;
    t3 = <ParcelCorner />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-overline text-primary">Featured package</p>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== item.name) {
    t5 = <FeaturedHeroTitle surface="package">{item.name}</FeaturedHeroTitle>;
    $[5] = item.name;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== item.description) {
    t6 = item.description && <FeaturedHeroDescription surface="package">{item.description}</FeaturedHeroDescription>;
    $[7] = item.description;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== item.packageIncludedItems) {
    t7 = (item.packageIncludedItems?.length ?? 0) > 0 && <div className="mt-6 max-w-3xl" data-testid="featured-package-items"><p id="featured-package-items-label" className="text-micro font-semibold uppercase tracking-[0.2em] text-primary/70">Includes</p><ul aria-labelledby="featured-package-items-label" className="mt-2 grid gap-x-8 gap-y-2 text-body text-foreground-secondary sm:grid-cols-2">{item.packageIncludedItems?.map(_temp)}</ul></div>;
    $[9] = item.packageIncludedItems;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t6 || $[13] !== t7) {
    t8 = <div className="flex-1">{t4}{t5}{t6}{t7}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== item) {
    t9 = <HeroActionButton surface="package" label="View package" onClick={() => setSelection(item)} />;
    $[15] = item;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== price || $[18] !== savings) {
    t10 = <HeroPriceSummary price={price} savings={savings} />;
    $[17] = price;
    $[18] = savings;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <FeaturedHeroFooter surface="package" action={t9}>{t10}</FeaturedHeroFooter>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t8) {
    t12 = <FeaturedItemHeroFrame surface="package" artwork={t2} corner={t3}><FeaturedHeroContent>{t8}{t11}</FeaturedHeroContent></FeaturedItemHeroFrame>;
    $[23] = t11;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== onAddLine) {
    t13 = selectedItem => {
      onAddLine(packageLineFromItem(selectedItem));
      setSelection(null);
    };
    $[26] = onAddLine;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setSelection(null);
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== selection || $[30] !== t13) {
    t15 = <PackageDetailsDialog item={selection} onConfirm={t13} onCancel={t14} />;
    $[29] = selection;
    $[30] = t13;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t12 || $[33] !== t15) {
    t16 = <>{t12}{t15}</>;
    $[32] = t12;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
};
const FeaturedMembershipHero: React.FC<FeaturedItemVariantProps> = t0 => {
  const $ = _c(40);
  const {
    item,
    onAddLine
  } = t0;
  const [selection, setSelection] = useState(null);
  let t1;
  if ($[0] !== item) {
    t1 = priceForShopItem(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const price = t1;
  const originalPrice = item.originalPrice ?? item.price;
  let t2;
  if ($[2] !== item) {
    t2 = savingsForShopItem(item);
    $[2] = item;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const savings = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      surface: "membership",
      eyebrow: "Featured membership",
      actionLabel: "Choose membership",
      Icon: FiRepeat,
      accentClassName: "bg-[radial-gradient(circle_at_18%_0%,rgb(var(--primary)_/_0.14),transparent_56%)]"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const variant = t3;
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <RegularHeroArtwork variant={variant} />;
    t5 = <RegularHeroCorner variant={variant} />;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className="text-overline text-primary">Featured membership</p>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== item.name) {
    t7 = <FeaturedHeroTitle surface="membership">{item.name}</FeaturedHeroTitle>;
    $[8] = item.name;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== item.description) {
    t8 = item.description && <FeaturedHeroDescription surface="membership">{item.description}</FeaturedHeroDescription>;
    $[10] = item.description;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = <div className="flex-1">{t6}{t7}{t8}</div>;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = item.alreadyMember ? "Already enrolled" : "Choose membership";
  let t11;
  if ($[15] !== item) {
    t11 = () => setSelection(item);
    $[15] = item;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== item.alreadyMember || $[18] !== t10 || $[19] !== t11) {
    t12 = <HeroActionButton surface="membership" label={t10} disabled={item.alreadyMember} onClick={t11} />;
    $[17] = item.alreadyMember;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== originalPrice || $[22] !== price || $[23] !== savings) {
    t13 = <HeroPriceSummary label="Initial charge" price={price} originalPrice={originalPrice} savings={savings} />;
    $[21] = originalPrice;
    $[22] = price;
    $[23] = savings;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t12 || $[26] !== t13) {
    t14 = <FeaturedHeroFooter surface="membership" action={t12}>{t13}</FeaturedHeroFooter>;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14 || $[29] !== t9) {
    t15 = <FeaturedItemHeroFrame surface="membership" artwork={t4} corner={t5}><FeaturedHeroContent>{t9}{t14}</FeaturedHeroContent></FeaturedItemHeroFrame>;
    $[28] = t14;
    $[29] = t9;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== onAddLine) {
    t16 = line => {
      onAddLine(line);
      setSelection(null);
    };
    $[31] = onAddLine;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setSelection(null);
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== selection || $[35] !== t16) {
    t18 = <MembershipConfiguratorDialog item={selection} onConfirm={t16} onCancel={t17} />;
    $[34] = selection;
    $[35] = t16;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t15 || $[38] !== t18) {
    t19 = <>{t15}{t18}</>;
    $[37] = t15;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  return t19;
};
const FeaturedRegularCatalogHero: React.FC<{
  item: RegularCatalogShopItem;
  onAddLine: (line: ShopCartLine) => void;
}> = t0 => {
  const $ = _c(55);
  const {
    item,
    onAddLine
  } = t0;
  const [selection, setSelection] = useState(null);
  const variant = REGULAR_HERO_VARIANTS[item.type];
  let t1;
  if ($[0] !== item) {
    t1 = priceForShopItem(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const price = t1;
  const originalPrice = item.originalPrice ?? item.price;
  let t2;
  if ($[2] !== item) {
    t2 = savingsForShopItem(item);
    $[2] = item;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const savings = t2;
  let t3;
  if ($[4] !== item) {
    t3 = getCatalogPromotionPresentation(item);
    $[4] = item;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const promotion = t3;
  let t4;
  let t5;
  if ($[6] !== variant) {
    t4 = <RegularHeroArtwork variant={variant} />;
    t5 = <RegularHeroCorner variant={variant} />;
    $[6] = variant;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== variant.eyebrow) {
    t6 = <p className="text-overline text-primary">{variant.eyebrow}</p>;
    $[9] = variant.eyebrow;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== item.name || $[12] !== variant.surface) {
    t7 = <FeaturedHeroTitle surface={variant.surface}>{item.name}</FeaturedHeroTitle>;
    $[11] = item.name;
    $[12] = variant.surface;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== item.description || $[15] !== variant.surface) {
    t8 = item.description && <FeaturedHeroDescription surface={variant.surface}>{item.description}</FeaturedHeroDescription>;
    $[14] = item.description;
    $[15] = variant.surface;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== promotion) {
    t9 = promotion?.kind === "available" && <p className="mt-5 flex max-w-prose items-start gap-2 text-body text-foreground-secondary" title={promotion.name}><FiTag aria-hidden={true} className="mt-0.5 h-4 w-4 flex-none text-primary" /><span><span className="font-semibold text-primary">{promotion.benefit}</span>{` · ${promotion.action}`}</span></p>;
    $[17] = promotion;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t6 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t10 = <div className="flex-1">{t6}{t7}{t8}{t9}</div>;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== item) {
    t11 = () => setSelection(item);
    $[24] = item;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== variant.actionLabel || $[28] !== variant.surface) {
    t12 = <HeroActionButton surface={variant.surface} label={variant.actionLabel} onClick={t11} />;
    $[26] = t11;
    $[27] = variant.actionLabel;
    $[28] = variant.surface;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== originalPrice || $[31] !== price || $[32] !== savings) {
    t13 = <HeroPriceSummary price={price} originalPrice={originalPrice} savings={savings} />;
    $[30] = originalPrice;
    $[31] = price;
    $[32] = savings;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t12 || $[35] !== t13 || $[36] !== variant.surface) {
    t14 = <FeaturedHeroFooter surface={variant.surface} action={t12}>{t13}</FeaturedHeroFooter>;
    $[34] = t12;
    $[35] = t13;
    $[36] = variant.surface;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t10 || $[39] !== t14) {
    t15 = <FeaturedHeroContent>{t10}{t14}</FeaturedHeroContent>;
    $[38] = t10;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t15 || $[42] !== t4 || $[43] !== t5 || $[44] !== variant.surface) {
    t16 = <FeaturedItemHeroFrame surface={variant.surface} artwork={t4} corner={t5}>{t15}</FeaturedItemHeroFrame>;
    $[41] = t15;
    $[42] = t4;
    $[43] = t5;
    $[44] = variant.surface;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== onAddLine) {
    t17 = selectedItem => {
      onAddLine(lineFromRegularItem(selectedItem));
      setSelection(null);
    };
    $[46] = onAddLine;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setSelection(null);
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== selection || $[50] !== t17) {
    t19 = <CatalogItemDetailsDialog item={selection} onConfirm={t17} onCancel={t18} />;
    $[49] = selection;
    $[50] = t17;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t16 || $[53] !== t19) {
    t20 = <>{t16}{t19}</>;
    $[52] = t16;
    $[53] = t19;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  return t20;
};
const FeaturedItemHero: React.FC<FeaturedItemVariantProps> = t0 => {
  const $ = _c(13);
  const {
    item,
    onAddLine
  } = t0;
  switch (item.type) {
    case ItemType.GIFT_CARD:
      {
        let t1;
        if ($[0] !== item || $[1] !== onAddLine) {
          t1 = <FeaturedGiftCardHero item={item} onAddLine={onAddLine} />;
          $[0] = item;
          $[1] = onAddLine;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    case ItemType.PACKAGE:
      {
        let t1;
        if ($[3] !== item || $[4] !== onAddLine) {
          t1 = <FeaturedPackageHero item={item} onAddLine={onAddLine} />;
          $[3] = item;
          $[4] = onAddLine;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case ItemType.MEMBERSHIP:
      {
        let t1;
        if ($[6] !== item || $[7] !== onAddLine) {
          t1 = <FeaturedMembershipHero item={item} onAddLine={onAddLine} />;
          $[6] = item;
          $[7] = onAddLine;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        return t1;
      }
    default:
      {
        if (isRegularCatalogShopItem(item)) {
          let t1;
          if ($[9] !== item || $[10] !== onAddLine) {
            t1 = <FeaturedRegularCatalogHero item={item} onAddLine={onAddLine} />;
            $[9] = item;
            $[10] = onAddLine;
            $[11] = t1;
          } else {
            t1 = $[11];
          }
          return t1;
        }
        let t1;
        if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <div role="status" className="mb-10 rounded-2xl border border-border bg-background-secondary p-4 text-body text-foreground-secondary">This item can't be featured here. Browse the shop below.</div>;
          $[12] = t1;
        } else {
          t1 = $[12];
        }
        return t1;
      }
  }
};
export const FeaturedItemHeroSkeleton: React.FC = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="sr-only">Loading featured shop item</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div role="status" aria-label="Loading featured shop item" className="relative mb-14 min-h-[300px] animate-pulse overflow-hidden rounded-3xl border border-border bg-background-secondary p-6 pt-16 motion-reduce:animate-none sm:p-8 sm:pt-16 md:min-h-[340px] md:p-12 md:pt-20">{t0}<div aria-hidden={true} className="flex h-full flex-col gap-4"><div className="h-3 w-28 rounded bg-muted" /><div className="h-10 w-4/5 max-w-2xl rounded bg-muted" /><div className="h-5 w-full max-w-xl rounded bg-muted" /><div className="h-5 w-2/3 max-w-md rounded bg-muted" /><div className="mt-auto h-16 w-full rounded bg-muted" /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export default FeaturedItemHero;
function _temp(includedItem, index) {
  return <li key={includedItem.itemId ?? `${includedItem.name}-${index}`} className="flex min-w-0 items-baseline gap-2"><span className="flex-none font-medium text-primary tabular-nums">{includedItem.quantity}×</span><span className="line-clamp-2">{includedItem.name}</span></li>;
}